import { useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import {
  logError,
  renderRadioOptions,
  scrollToTop
} from '../../common/HelperMethods';
import Checkbox from '../../common/Checkbox/Checkbox';
import { List } from '../../common/Widgets/List';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import UpdatePreferencesResponse from './UpdatePreferencesResponse';
import { MarketingPreferencesResponse } from '../../../api/Models/InsuranceJourney';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';

const listIcon = <FontAwesomeIcon icon={faCheck} listItem />;

type PageErrors = {
  iAgree: string;
  keepingYouInformed: string;
  keepingYouInformedOptions: string;
  nextYearInsurance: string;
  yourDocuments: string;
};

const initialFormErrors: PageErrors = {
  iAgree: '',
  keepingYouInformed: '',
  keepingYouInformedOptions: '',
  nextYearInsurance: '',
  yourDocuments: ''
};

const privacyPromiseItems = [
  'We keep your data safe and secure.',
  'We don’t sell your data.',
  'You can manage and review you marketing preferences choices at any time.',
  'Your data will be used in accordance with our fair use policy.',
  'This doesn’t affect how we will contact you to either manage your current insurance policy with us or your accident claim.'
];

const PostcodeConfirmationPage = () => {
  const { updateMarketingPreferences } = useCarInsuranceController();
  const [updatePreferencesResponse, setUpdatePreferencesResponse] = useState<
    boolean | undefined
  >(undefined);

  const { state } = useLocation();
  const { firstName, postCode, marketingPreferences } =
    state as MarketingPreferencesResponse;

  const {
    contactByEmail,
    contactByPhone,
    contactByPost,
    contactByText,
    isMarketingWanted,
    partnerId
  } = marketingPreferences;

  const [keepingYouInformed, setIsMarketingWanted] =
    useState(isMarketingWanted);
  const [marketingViaEmail, setContactByEmail] = useState(contactByEmail);
  const [marketingViaPhone, setContactByPhone] = useState(contactByPhone);
  const [marketingViaPost, setContactByPost] = useState(contactByPost);
  const [marketingViaTextMessage, setContactByText] = useState(contactByText);
  const [formErrors, setFormErrors] = useState<PageErrors>(initialFormErrors);

  const navigate = useNavigate();
  const { site } = useContext(SiteContext);

  useEffect(() => {
    scrollToTop();
  }, []);

  const onBackClick = () => {
    navigate(-1);
  };

  const validate = (): boolean => {
    let isValid = true;
    const updatedFormErrors = { ...initialFormErrors };

    if (keepingYouInformed === undefined) {
      updatedFormErrors.keepingYouInformed = 'This field is required';
      isValid = false;
    } else if (keepingYouInformed) {
      if (
        marketingViaEmail === false &&
        marketingViaPhone === false &&
        marketingViaPost === false &&
        marketingViaTextMessage === false
      ) {
        updatedFormErrors.keepingYouInformedOptions =
          'At least one option is required';
        isValid = false;
      }
    }

    setFormErrors(updatedFormErrors);
    return isValid;
  };

  const onSubmit = () => {
    const validForm = validate();

    if (validForm) {
      updateMarketingPreferences(
        {
          isMarketingWanted: keepingYouInformed,
          contactByEmail: marketingViaEmail,
          contactByPhone: marketingViaPhone,
          contactByPost: marketingViaPost,
          contactByText: marketingViaTextMessage,
          partnerId
        },
        site.user.token,
        (flag: boolean) => {
          setUpdatePreferencesResponse(flag);
        },
        logError
      );
    }
  };

  const onChangeIsMarketingWanted = (value: string) => {
    setIsMarketingWanted(value === 'Yes' ? true : false);
  };

  return (
    <>
      {updatePreferencesResponse !== undefined ? (
        <UpdatePreferencesResponse
          customerName={firstName}
          responseFlag={updatePreferencesResponse}
        />
      ) : (
        <>
          <div className='one-col'>
            <h4 className='section-header'>Hello {firstName}</h4>

            <p>
              Last 3 digits of Postcode: {postCode?.slice(-3).toUpperCase()}
            </p>

            <p>
              If the above details are incorrect, please call us on{' '}
              {site.brand.brandPhoneNumbers.quotesAndRenewalsNumber}
            </p>

            <p>
              Would you like to receive updates and special promotions about our
              insurance, accident aftercare, excess return and related products
              and services in the future?
            </p>
          </div>
          <div className='one-col'>
            {renderRadioOptions(
              ['Yes', 'No'],
              'isMarketingWanted',
              keepingYouInformed ? 'Yes' : 'No',
              onChangeIsMarketingWanted
            )}
            {formErrors.keepingYouInformed && (
              <div className='error-style'>{formErrors.keepingYouInformed}</div>
            )}
          </div>
          {keepingYouInformed === true && (
            <>
              <p>
                <h5 className='section-header m2-top'>
                  Tell us how you’d prefer to be contacted:
                </h5>
              </p>
              <div className='one-col modify-label-order'>
                <Checkbox
                  id='chk-marketing-email'
                  checked={marketingViaEmail}
                  text='Email (or other electronic means)'
                  onChange={() => setContactByEmail(!marketingViaEmail)}
                />
                <Checkbox
                  id='chk-marketing-post'
                  checked={marketingViaPost}
                  text='Post'
                  onChange={() => setContactByPost(!marketingViaPost)}
                />
                <Checkbox
                  id='chk-marketing-phone'
                  checked={marketingViaPhone}
                  text='Telephone'
                  onChange={() => setContactByPhone(!marketingViaPhone)}
                />

                <Checkbox
                  id='chk-marketing-text'
                  checked={marketingViaTextMessage}
                  text='Text message'
                  onChange={() => setContactByText(!marketingViaTextMessage)}
                />
                {formErrors.keepingYouInformedOptions && (
                  <div className='error-style'>
                    {formErrors.keepingYouInformedOptions}
                  </div>
                )}
              </div>
            </>
          )}
          <div className='one-col'>
            <h5 className='section-header m2-top'>
              Our privacy promise to you:
            </h5>
            <div className='flex-row'>
              <div className='m2-right'>
                <List
                  id='quote-list-table1'
                  listItems={privacyPromiseItems}
                  icon={listIcon}
                />
              </div>
            </div>
          </div>
          <div className='even-space-out flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-next'
              text='Submit'
              onClick={onSubmit}
              additionalClassNames='btn-continue'
            />
          </div>
        </>
      )}
    </>
  );
};

export default PostcodeConfirmationPage;
